// What? A component to display resource usage diagnostics.
// Why? To separate diagnostics display logic from the main dashboard.
// How? Renders resource usage metrics with progress bars and status messages.

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  LinearProgress,
  Chip,
  Divider,
} from "@mui/joy";
import Loading from "../Loading";
import { moduleRegistry } from "../../modules/moduleRegistry";

interface ResourceUsage {
  resources: Array<{
    name: string;
    used: number;
    total: number;
    percentage: number;
  }>;
}

interface UserUsage {
  organization_id: string | null;
  period_start: string | null;
  period_end: string | null;
  metrics: Array<{
    module: string;
    metric: string;
    current_usage: number;
  }>;
}

interface DiagnosticsCardProps {
  resourceUsage: ResourceUsage | null;
  userUsage: UserUsage | null;
  loading: boolean;
  loadingUserUsage: boolean;
  isAdmin: boolean;
}

const DiagnosticsCard: React.FC<DiagnosticsCardProps> = ({
  resourceUsage,
  userUsage,
  loading,
  loadingUserUsage,
  isAdmin,
}) => {
  // Format number with K/M suffix for large numbers
  const formatNumber = (num: number): string => {
    if (num == null) return "0";
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toFixed(2);
  };

  // Format metric name for display
  const formatMetricName = (metric: string): string => {
    return metric
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get module display name
  const getModuleDisplayName = (moduleName: string): string => {
    return moduleRegistry[moduleName]?.name || moduleName;
  };

  // Group user metrics by module
  const metricsByModule = (userUsage?.metrics || []).reduce(
    (acc, metric) => {
      if (!acc[metric.module]) {
        acc[metric.module] = [];
      }
      acc[metric.module].push(metric);
      return acc;
    },
    {} as Record<
      string,
      Array<{ module: string; metric: string; current_usage: number }>
    >,
  );

  return (
    <Card>
      <CardContent>
        <Typography level="h3" sx={{ mb: 3 }}>
          Usage & Diagnostics
        </Typography>

        <Stack spacing={3}>
          {/* My Usage Section - Shown to all users */}
          <Box>
            <Typography level="title-lg" sx={{ fontWeight: 600, mb: 2 }}>
              My Usage
            </Typography>
            {loadingUserUsage ? (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <Loading />
              </Box>
            ) : userUsage &&
              userUsage.metrics &&
              userUsage.metrics.length > 0 ? (
              <Stack spacing={2.5}>
                {/* Billing Period Info */}
                {userUsage.period_start && userUsage.period_end && (
                  <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                    Billing Period:{" "}
                    {new Date(userUsage.period_start).toLocaleDateString()} -{" "}
                    {new Date(userUsage.period_end).toLocaleDateString()}
                  </Typography>
                )}

                {/* Group metrics by module */}
                {Object.entries(metricsByModule).map(
                  ([moduleName, metrics]) => (
                    <Box key={moduleName} sx={{ mb: 2 }}>
                      <Typography
                        level="body-md"
                        fontWeight="md"
                        sx={{ mb: 1.5, color: "primary.600" }}
                      >
                        {getModuleDisplayName(moduleName)}
                      </Typography>
                      <Stack spacing={2}>
                        {metrics.map((metric, idx) => (
                          <Box key={`${moduleName}-${metric.metric}-${idx}`}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 0.5 }}
                            >
                              <Typography level="body-sm" fontWeight="md">
                                {formatMetricName(metric.metric)}
                              </Typography>
                              <Typography level="body-sm" color="neutral">
                                {formatNumber(metric.current_usage)}
                              </Typography>
                            </Stack>
                            {/* Note: Personal usage doesn't show limits, so no progress bar */}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ),
                )}
              </Stack>
            ) : (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <Typography level="body-sm" color="neutral">
                  No personal usage data available.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Organization Usage Section - Only for admins */}
          {isAdmin && (
            <>
              <Divider />
              <Box>
                <Typography level="title-lg" sx={{ fontWeight: 600, mb: 2 }}>
                  Organization Usage
                </Typography>
                {loading ? (
                  <Box sx={{ py: 2, textAlign: "center" }}>
                    <Loading />
                  </Box>
                ) : resourceUsage && resourceUsage.resources ? (
                  <Stack spacing={3}>
                    {resourceUsage.resources.map(
                      (resource: any, index: number) => {
                        // Determine unit and format based on resource name
                        let unit = "";
                        let formattedUsed = resource.used;
                        let formattedTotal = resource.total;

                        if (resource.name === "Storage") {
                          unit = "GB";
                          formattedUsed = resource.used.toFixed(2);
                          formattedTotal = resource.total.toFixed(2);
                        } else if (resource.name === "Requests") {
                          unit = "";
                          formattedUsed = formatNumber(resource.used);
                          formattedTotal = formatNumber(resource.total);
                        } else if (resource.name === "Workspaces") {
                          unit = "";
                          formattedUsed = resource.used.toString();
                          formattedTotal = resource.total.toString();
                        }

                        // Determine status message and color based on percentage
                        let status = "";
                        let statusColor: "success" | "warning" | "danger" =
                          "success";

                        if (resource.percentage >= 90) {
                          status = "Almost at limit! Consider upgrading.";
                          statusColor = "danger";
                        } else if (resource.percentage >= 75) {
                          status = "Approaching limit. Consider upgrading.";
                          statusColor = "warning";
                        }

                        return (
                          <Box key={index}>
                            <Typography
                              level="body-md"
                              fontWeight="lg"
                              sx={{ mb: 1 }}
                            >
                              {resource.name}
                            </Typography>

                            <LinearProgress
                              value={Math.min(resource.percentage, 100)}
                              color={
                                resource.percentage >= 90
                                  ? "danger"
                                  : resource.percentage >= 75
                                    ? "warning"
                                    : "success"
                              }
                              variant="solid"
                              determinate
                              sx={{ mb: 1 }}
                            />

                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              flexWrap="wrap"
                              gap={1}
                              sx={{ mt: 1 }}
                            >
                              <Typography level="body-sm" color="neutral">
                                {formattedUsed}
                                {unit ? ` ${unit}` : ""} / {formattedTotal}
                                {unit ? ` ${unit}` : ""} ({resource.percentage}
                                %)
                              </Typography>
                              {status && (
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  color={statusColor}
                                >
                                  {statusColor === "warning" ? "⚠️" : "🔴"}{" "}
                                  {status}
                                </Chip>
                              )}
                            </Stack>
                          </Box>
                        );
                      },
                    )}
                  </Stack>
                ) : (
                  <Box sx={{ py: 2, textAlign: "center" }}>
                    <Typography level="body-sm" color="neutral">
                      No organization usage data available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DiagnosticsCard;
